import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import { Chart } from 'chart.js';

import get from 'lodash/get';
import isFunction from 'lodash/isFunction';

import useChartData from '../ChartContext';

import { getOnPointClickOptions, getXValueByChartPosition } from '../helpers/chartOption.helpers';

export const ChartCanvas = ({ formatXLabel, onPointClick, minHeight }) => {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  const {
    chartID, datasets, labels, chartConfig, chartType, maxY, options
  } = useChartData()

  const xAxisFormatter = useCallback((value) => {
    const selectedLabel = get(labels, `[${value}]`);
    if (!selectedLabel) {
      return '';
    }

    return formatXLabel(selectedLabel)
  }, [formatXLabel, labels]);

  const handlePointClick = useCallback((e) => {
    const dataX = getXValueByChartPosition(e, chartInstance.current);
    onPointClick(dataX);
  }, [onPointClick])

  // Initialize the Chart
  useLayoutEffect(() => {
    if (!datasets.length || !canvasRef.current) {
      return () => { };
    }

    const hasClickFunction = isFunction(onPointClick);
    if (!chartInstance.current) {
      const optionsWithClick = hasClickFunction ? {
        ...chartConfig,
        ...getOnPointClickOptions(handlePointClick),
      } : chartConfig;

      chartInstance.current = new Chart(canvasRef.current, {
        type: chartType,
        data: {
          labels,
          datasets,
        },
        options: { ...optionsWithClick, ...options },
      });
    } else if (hasClickFunction) {
      chartInstance.current.options.onClick = handlePointClick;
      chartInstance.current.update();
    }

    return () => { };
  }, [chartConfig, chartType, datasets, labels, handlePointClick, onPointClick, options]);

  // Handles Updates without destroying the chart or rerunning animations
  useLayoutEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.data = {
        labels,
        datasets,
      }

      chartInstance.current.options.scales.y.max = maxY;
      chartInstance.current.update();
    }

  }, [datasets, labels, maxY]);

  // Assign X Axis Formatter Function
  useLayoutEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.options.scales.x.ticks.callback = xAxisFormatter
    }
  }, [xAxisFormatter])

  // Destroy the chart as needed
  useEffect(() => () => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }
  }, [chartID])

  return (
    <canvas id={chartID} ref={canvasRef} style={{ minHeight }} />
  )
}

export default ChartCanvas;
